/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Group} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {titleCase} from '@ion/core/src/utils/StringUtil';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import {useShowDeletePrompt} from '@ion/dls/src/hooks/useShowDeletePrompt';
import {DeleteResultPrompt} from '@ion/features/src/prompts/DeleteResultPrompt';

// defines the GroupDeletePrompt Prop type definitions ...
export interface GroupDeletePromptProps {
  show: boolean;
  group: Group | null;
  result?: Group | null;
  loading?: boolean;
  error?: ServiceError | null;
  overrideMessage?: Function | null;
  onDeleteConfirm: (group: Group) => void;
  onDeleteCancel: () => void;
}

/**
 * The GroupDeletePrompt provides the UI for prompting, confirming, or cancelling an Group
 * Delete action.
 *
 * ```typescript
 *
 *    import {GroupDeletePrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({adminGroups: {destroy}}) => destroy);
 *
 *    const onDeleteConfirm = function(Group: EnhancedGroup) {
 *      dispatch(deleteGroup(selected));
 *    };
 *
 *    function onDeleteCancel() {
 *      dispatch(deleteAdminGroup(selected, false));
 *    }
 *
 *    return (
 *      <GroupDeletePrompt show={isEditing}
 *        Group={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}            show When true, the Group delete prompt will be displayed.
 * @param {EnhancedGroup}      group The selected Group that is to be deleted.
 * @param {EnhancedGroup}      result The Group returned by the service after it has been deleted.
 * @param {boolean}            loading When true, indicates that the Group is currently being
 *                             deleted.
 * @param {ServiceError}       error A reference to the ServiceError if the deletion fails.
 * @param {Function}           onDeleteConfirm callback function which will be invoked with the
 *                             Group when the user confirms the deletion.
 * @param {Function}           onDeleteCancel callback function which will be invoked if the user
 *                             cancels the delete operation.
 */
export const AdminGroupDeletePrompt = ({
  show,
  group,
  result,
  loading,
  error,
  onDeleteConfirm,
  onDeleteCancel,
  overrideMessage = null
}: GroupDeletePromptProps) => {
  const {setShowError, setShowSuccess, showSuccess, showError, closeSnack} = useShowDeletePrompt();

  useEffect(() => {
    setShowError(!!error);
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowSuccess(true);
    }
  }, [result]);

  if (show || showError || showSuccess) {
    const entityDesc = translate('common:entityLabels.group');
    return (
      <>
        {show && (
          <DeletePrompt show={show}
            entity={entityDesc}
            items={group}
            isLoading={loading}
            overrideMessage={overrideMessage}
            onConfirm={function() {
              onDeleteConfirm(group);
            }}
            onCancel={function() {
              onDeleteCancel();
            }} />
        )}
        <DeleteResultPrompt entityTitle={titleCase(entityDesc)}
          error={error}
          isError={showError}
          isSuccess={showSuccess}
          onAction={closeSnack} />
      </>
    );
  }
  return null;
};

export default AdminGroupDeletePrompt;
